/* ==========================================================================
   Mirah — facets.js
   <facet-controller>: Search & Discovery filtering without reloads, instant
   Colour / Details / category-tab filtering, load-more, mobile filter sheet.
   Server swap mirrors the reference: results out .3s / in .5s (expo.inOut),
   cards mount scale 0 rotate -10° → 1 0°. Instant filters re-flow the grid
   with a FLIP: leavers shrink out, stayers glide to their new cells, joiners
   rise in with a stagger.
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var Theme = window.Theme || (window.Theme = {});
  var motion = function () { return Theme.motionOn && window.gsap; };
  var slice = function (list) { return Array.prototype.slice.call(list); };
  var LOCAL = ['colour', 'detail'];

  // The photo inside each card settles from a slight zoom while the card itself pops in.
  function frames(nodes) {
    return nodes.map(function (n) { return n.querySelector('.product-card__frame'); }).filter(Boolean);
  }
  function settle(nodes) {
    var f = frames(nodes);
    if (f.length) gsap.fromTo(f, { scale: 1.25 }, { scale: 1, ease: 'expo.out', duration: 1.6, stagger: 0.1, clearProps: 'transform' });
  }

  function mountCards(nodes, withScroll) {
    if (!motion() || !nodes.length) return;
    if (withScroll && window.ScrollTrigger) {
      gsap.set(nodes, { scale: 0, rotate: -10 });
      ScrollTrigger.batch(nodes, {
        start: 'top bottom-=5%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { scale: 1, rotate: 0, ease: 'expo.out', duration: 1.2, stagger: 0.1, overwrite: true });
          settle(batch);
        }
      });
      return;
    }
    gsap.fromTo(nodes, { scale: 0, rotate: -10 }, { scale: 1, rotate: 0, ease: 'expo.out', duration: 1.2, stagger: 0.1 });
    settle(nodes);
  }

  class FacetController extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.getAttribute('data-section-id');
      this.sheet = this.querySelector('[data-filter-sheet]');
      this.localTabs = this.hasAttribute('data-local-tabs');
      this.busy = false;
      var self = this;

      this.addEventListener('change', function (e) {
        if (e.target.hasAttribute('data-local')) { self.syncLocalInputs(e.target); self.applyLocal(true, true); return; }
        var form = e.target.closest('[data-facet-form]');
        var sort = e.target.closest('[data-sort]');
        if (sort) return self.apply(self.queryFrom(self.querySelector('#FacetsDesktop')));
        if (!form) return;
        if (form.id === 'FacetsMobile') return; // applied with the button
        if (e.target.type === 'number') return; // price inputs apply on blur/enter
        self.apply(self.queryFrom(form));
      });
      this.addEventListener('focusout', function (e) {
        if (e.target.type === 'number' && e.target.closest('#FacetsDesktop')) self.apply(self.queryFrom(e.target.form));
      });
      this.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-facet-form]');
        if (!form) return;
        e.preventDefault();
        self.apply(self.queryFrom(form));
        self.closeSheet();
      });
      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-coll-tab]');
        if (tab && self.localTabs) { e.preventDefault(); e.stopPropagation(); self.setTab(tab.getAttribute('data-coll-tab'), true); return; }
        if (e.target.closest('[data-local-clear]')) { e.preventDefault(); self.clearLocal(); return; }
        var link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); self.apply(new URL(link.href, window.location.origin).search.slice(1)); self.closeSheet(); return; }
        var more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); self.loadMore(more); return; }
        if (e.target.closest('[data-filter-open]')) { self.openSheet(); return; }
        if (e.target.closest('[data-filter-close]')) { self.closeSheet(); return; }
        if (e.target.closest('[data-filter-apply]')) { self.apply(self.queryFrom(self.querySelector('#FacetsMobile'))); self.closeSheet(); }
      }, true);
      this.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.closeSheet(); });
      window.addEventListener('popstate', function () { self.render(window.location.search.slice(1), false); });
      window.addEventListener('resize', Theme.debounce ? Theme.debounce(function () { self.movePill(true); }, 150) : function () {});

      // Restore instant filters from the URL (e.g. the megamenu's "Shop by colour" links), then mount.
      this.readLocalFromUrl();
      this.tab = this.localTabs ? (new URLSearchParams(window.location.search).get('tab') || '') : null;
      this.markTabs();
      this.applyLocal(false, false);
      requestAnimationFrame(function () { self.movePill(true); });
      var ready = Theme.ready || Promise.resolve();
      ready.then(function () {
        self.movePill(true);
        mountCards(self.visibleItems(), true);
      });
    }

    /* ---------- Instant filters ---------- */
    items() { return slice(this.querySelectorAll('[data-card-grid] [data-card-item]')); }
    visibleItems() { return this.items().filter(function (it) { return !it.hidden; }); }

    localState() {
      var form = this.querySelector('#FacetsDesktop') || this.querySelector('[data-facet-form]');
      var state = {};
      LOCAL.forEach(function (k) {
        state[k] = form ? slice(form.querySelectorAll('input[data-local][name="' + k + '"]:checked')).map(function (i) { return i.value; }) : [];
      });
      return state;
    }

    readLocalFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var self = this;
      LOCAL.forEach(function (k) {
        var vals = params.getAll(k).join(',').split(',').filter(Boolean);
        slice(self.querySelectorAll('input[data-local][name="' + k + '"]')).forEach(function (i) { i.checked = vals.indexOf(i.value) > -1; });
      });
    }

    syncLocalInputs(src) {
      slice(this.querySelectorAll('input[data-local][name="' + src.name + '"][value="' + src.value + '"]')).forEach(function (i) { if (i !== src) i.checked = src.checked; });
    }

    clearLocal() {
      slice(this.querySelectorAll('input[data-local]')).forEach(function (i) { i.checked = false; });
      if (this.localTabs) this.tab = '';
      this.markTabs();
      this.movePill(false);
      this.applyLocal(true, true);
    }

    setTab(handle, animate) {
      if (handle === this.tab) return;
      this.tab = handle;
      this.markTabs();
      this.movePill(!animate);
      this.applyLocal(animate, true);
      if (Theme.scrollTo) {
        var top = this.getBoundingClientRect().top;
        if (top < 0) Theme.scrollTo(this, { offset: -100, duration: 1 });
      }
    }

    markTabs() {
      var self = this;
      if (!this.localTabs) return;
      slice(this.querySelectorAll('[data-coll-tab]')).forEach(function (t) {
        var on = t.getAttribute('data-coll-tab') === (self.tab || '');
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'page'); else t.removeAttribute('aria-current');
      });
      var title = this.querySelector('[data-grid-title]');
      var active = this.querySelector('[data-coll-tab].is-active span');
      if (title) title.textContent = this.tab && active ? active.textContent : title.getAttribute('data-all-title');
    }

    movePill(instant) {
      var pill = this.querySelector('[data-coll-tabs-pill]');
      var tab = this.querySelector('[data-coll-tab].is-active');
      if (!pill) return;
      if (!tab) { pill.style.opacity = 0; return; }
      pill.style.opacity = 1;
      var vars = { x: tab.offsetLeft, width: tab.offsetWidth };
      if (instant || !motion()) { if (window.gsap) gsap.set(pill, vars); else { pill.style.width = vars.width + 'px'; pill.style.transform = 'translateX(' + vars.x + 'px)'; } return; }
      gsap.to(pill, Object.assign({ duration: 0.9, ease: 'expo.out' }, vars));
      // Keep the active tab in view on phones, where the track scrolls sideways.
      var track = tab.parentElement;
      if (track.scrollWidth > track.clientWidth) track.scrollTo({ left: tab.offsetLeft - (track.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
    }

    matches(item, state) {
      var card = item.querySelector('[data-product-card]');
      if (!card) return true;
      if (this.tab) {
        var cols = (card.getAttribute('data-collections') || '').split(' ');
        if (cols.indexOf(this.tab) < 0) return false;
      }
      if (state.colour.length && state.colour.indexOf(card.getAttribute('data-colour')) < 0) return false;
      if (state.detail.length) {
        var det = (card.getAttribute('data-details') || '').split(' ');
        // Every chosen detail must be present (lace + headscarf narrows, it doesn't widen).
        for (var i = 0; i < state.detail.length; i++) if (det.indexOf(state.detail[i]) < 0) return false;
      }
      return true;
    }

    applyLocal(animate, push) {
      var grid = this.querySelector('[data-card-grid]');
      if (!grid) return;
      var self = this;
      var state = this.localState();
      var items = this.items();
      var active = state.colour.length + state.detail.length;

      // URL: keep server params, rewrite ours.
      if (push) {
        var url = new URL(window.location.href);
        LOCAL.forEach(function (k) { url.searchParams.delete(k); if (state[k].length) url.searchParams.set(k, state[k].join(',')); });
        if (this.localTabs) { url.searchParams.delete('tab'); if (this.tab) url.searchParams.set('tab', this.tab); }
        window.history.replaceState({}, '', url.toString());
      }

      // Counts per value (against everything else that's selected) so dead ends read as 0.
      slice(this.querySelectorAll('[data-local-count]')).forEach(function (el) {
        var parts = el.getAttribute('data-local-count').split(':');
        var probe = { colour: parts[0] === 'colour' ? [parts[1]] : state.colour, detail: state.detail };
        var n = items.filter(function (it) { return self.matches(it, probe); }).length;
        el.textContent = n;
        el.closest('label').classList.toggle('is-disabled', n === 0 && state.colour.indexOf(parts[1]) < 0);
      });
      slice(this.querySelectorAll('[data-local-clear]')).forEach(function (b) { if (b.closest('.facets__head')) b.hidden = !active && !self.tab; });
      var badge = this.querySelector('[data-filter-count]');
      if (badge) {
        var server = parseInt(badge.getAttribute('data-server') || badge.textContent, 10) || 0;
        if (!badge.hasAttribute('data-server')) badge.setAttribute('data-server', server);
        badge.textContent = server + active;
        badge.hidden = server + active === 0;
      }

      var show = [], hide = [];
      items.forEach(function (it) { (self.matches(it, state) ? show : hide).push(it); });
      var count = this.querySelector('[data-results-count]');
      if (count) count.textContent = (show.length === 1 ? count.getAttribute('data-one') : count.getAttribute('data-other')).replace(/\d+/, show.length);
      var empty = this.querySelector('[data-local-empty]');
      if (empty) empty.hidden = show.length > 0 || !items.length;

      var leaving = hide.filter(function (it) { return !it.hidden; });
      var joining = show.filter(function (it) { return it.hidden; });
      var staying = show.filter(function (it) { return !it.hidden; });
      if (!animate || !motion() || (!leaving.length && !joining.length)) {
        hide.forEach(function (it) { it.hidden = true; });
        show.forEach(function (it) { it.hidden = false; });
        if (window.ScrollTrigger) ScrollTrigger.refresh();
        return;
      }

      var first = new Map();
      staying.forEach(function (it) { first.set(it, it.getBoundingClientRect()); });
      var swap = function () {
        leaving.forEach(function (it) { it.hidden = true; gsap.set(it, { clearProps: 'opacity' }); });
        joining.forEach(function (it) { it.hidden = false; });
        staying.forEach(function (it) {
          var a = first.get(it), b = it.getBoundingClientRect();
          gsap.fromTo(it, { x: a.left - b.left, y: a.top - b.top }, { x: 0, y: 0, duration: 1, ease: 'expo.out', overwrite: 'auto' });
        });
        if (joining.length) {
          gsap.fromTo(joining, { opacity: 0, scale: 0.86, y: 40, rotate: -4 }, { opacity: 1, scale: 1, y: 0, rotate: 0, duration: 1.1, ease: 'expo.out', stagger: 0.06, delay: 0.08 });
          settle(joining);
        }
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      };
      if (leaving.length) gsap.to(leaving, { opacity: 0, scale: 0.86, duration: 0.35, ease: 'power2.in', stagger: 0.02, onComplete: swap });
      else swap();
    }

    /* ---------- Server filters (Section Rendering) ---------- */
    queryFrom(form) {
      if (!form) return '';
      var params = new URLSearchParams();
      new FormData(form).forEach(function (v, k) { if (v !== '') params.append(k, v); });
      // Instant filters ride along as one comma list each, so the URL stays shareable.
      LOCAL.forEach(function (k) { var all = params.getAll(k); params.delete(k); if (all.length) params.set(k, all.join(',')); });
      if (this.localTabs && this.tab) params.set('tab', this.tab);
      var sort = this.querySelector('[data-sort]');
      if (sort && sort.value && sort.value !== sort.getAttribute('data-default')) params.set('sort_by', sort.value);
      else params.delete('sort_by');
      return params.toString();
    }

    apply(query) {
      this.render(query, true);
    }

    render(query, push) {
      if (this.busy) return;
      this.busy = true;
      var self = this;
      var results = this.querySelector('[data-results]');
      var url = window.location.pathname + (query ? '?' + query : '');
      var fetchUrl = window.location.pathname + '?' + (query ? query + '&' : '') + 'section_id=' + this.sectionId;

      if (Theme.scrollTo) Theme.scrollTo(this, { offset: -120, duration: 1 });
      var out = motion() ? new Promise(function (r) { gsap.to(results, { opacity: 0, y: 12, duration: 0.3, ease: 'expo.inOut', onComplete: r }); }) : Promise.resolve();
      var load = fetch(fetchUrl).then(function (r) { return r.text(); });

      Promise.all([load, out]).then(function (res) {
        var doc = new DOMParser().parseFromString(res[0], 'text/html');
        var fresh = doc.querySelector('facet-controller');
        if (!fresh) { window.location.href = url; return; }
        ['[data-results]', '[data-facets-desktop]', '[data-facets-mobile]', '.filter-toggle'].forEach(function (sel) {
          var a = self.querySelector(sel), b = fresh.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        });
        if (push) window.history.pushState({}, '', url);
        if (Theme.initAccordions) Theme.initAccordions(self);
        if (Theme.iconClone) Theme.iconClone(self);
        if (window.MirahRails) window.MirahRails.init(self);
        if (Theme.initMotion) Theme.initMotion(self.querySelector('[data-results]'));
        self.readLocalFromUrl();
        self.tab = self.localTabs ? (new URLSearchParams(window.location.search).get('tab') || '') : null;
        self.markTabs();
        self.applyLocal(false, false);
        var r = self.querySelector('[data-results]');
        if (motion()) gsap.fromTo(r, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.6, ease: 'expo.out' });
        mountCards(self.visibleItems().slice(0, 9), false);
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      }).catch(function () { window.location.href = url; })
        .finally(function () { self.busy = false; });
    }

    loadMore(link) {
      if (this.busy) return;
      this.busy = true;
      link.classList.add('is-loading');
      var self = this;
      var u = new URL(link.href, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      fetch(u.toString()).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = self.querySelector('[data-card-grid]');
        var items = slice(doc.querySelectorAll('[data-card-grid] [data-card-item]'));
        items.forEach(function (it) { grid.appendChild(it); });
        var wrap = self.querySelector('[data-load-more-wrap]');
        var next = doc.querySelector('[data-load-more-wrap]');
        if (wrap) { if (next) wrap.innerHTML = next.innerHTML; else wrap.remove(); }
        if (Theme.iconClone) Theme.iconClone(grid);
        self.applyLocal(false, false);
        mountCards(items.filter(function (it) { return !it.hidden; }), false);
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      }).finally(function () { self.busy = false; link.classList.remove('is-loading'); });
    }

    openSheet() {
      if (!this.sheet) return;
      this.sheet.classList.add('is-open');
      this.sheet.setAttribute('aria-hidden', 'false');
      var panel = this.sheet.querySelector('.filter-sheet__panel');
      if (Theme.lockScroll) Theme.lockScroll();
      // The CSS parks the panel at translateY(100%); GSAP would read that as a pixel y and keep it under the percentage,
      // so reset y explicitly and animate the percentage alone.
      if (motion()) gsap.fromTo(panel, { y: 0, yPercent: 100 }, { y: 0, yPercent: 0, duration: 0.5, ease: 'expo.out', overwrite: true });
      else panel.style.transform = 'none';
      if (Theme.trapFocus) Theme.trapFocus(panel);
    }

    closeSheet() {
      if (!this.sheet || !this.sheet.classList.contains('is-open')) return;
      var sheet = this.sheet;
      var panel = sheet.querySelector('.filter-sheet__panel');
      var done = function () {
        sheet.classList.remove('is-open'); sheet.setAttribute('aria-hidden', 'true');
        if (window.gsap) gsap.set(panel, { clearProps: 'transform' }); // back to the CSS resting position
      };
      if (motion()) gsap.to(panel, { y: 0, yPercent: 100, duration: 0.4, ease: 'expo.in', overwrite: true, onComplete: done });
      else { panel.style.transform = ''; done(); }
      if (Theme.unlockScroll) Theme.unlockScroll();
      if (Theme.releaseFocus) Theme.releaseFocus();
    }
  }
  if (!customElements.get('facet-controller')) customElements.define('facet-controller', FacetController);
})();
